import { InfoPopover, RiCompass4Line, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { usePathSet } from '@/hooks/usePathSet';
import { useProjectId } from '@/pages/shared/hooks/useProjectId';
import {
  FactCapsule,
  FactCapsuleTrend,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useStaticImage } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useStaticImage';
import { LandMonitoringCard } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/project/pages/components/LandMonitoringCard';
import { buildPath } from '@/utils/buildPath';

export const NDVITile = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();
  const projectId = useProjectId();

  const ndviMapImage = useStaticImage('ndvi');
  const ndviBaselineYear = useFact('r1_conservation_ndvi_baseline_year');

  const ndviAverageCurrent = useFact('r1_conservation_ndvi_i');
  const ndviChangePct = useFact('r1_conservation_ndvi_change_percentage');
  const ndviBaseline = useFact('r1_conservation_ndvi_baseline');

  return (
    <LandMonitoringCard
      data-testid='ndvi'
      title={t('shared.ncaDetail.details.landMonitoring.ndvi.title')}
      heading={{
        heading: (
          <ChartHeading
            heading={t('shared.ncaDetail.details.landMonitoring.ndvi.heading')}
            popover={
              <InfoPopover
                title={t('shared.ncaDetail.details.landMonitoring.ndvi.explainers.fromNdvi.title')}
                body={t('shared.ncaDetail.details.landMonitoring.ndvi.explainers.fromNdvi.body')}
                popoverTriggerProps={{
                  className: 'text-text-secondary',
                }}
              />
            }
          />
        ),
      }}
      image={{
        url: ndviMapImage,
        alt: t('shared.ncaDetail.details.landMonitoring.ndvi.mapImageAlt'),
      }}
      detailsPagePath={buildPath(pathSet.ndvi, { pathParams: { projectId } })}
      stats={
        <Stack spacing={6}>
          <Stack spacing={10} className='py-3'>
            <FactCapsule
              label={t('shared.ncaDetail.details.ecologicalIntegrity.labels.projectAverageValue')}
              capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
              fact={ndviAverageCurrent}
              trend={<FactCapsuleTrend trend={ndviChangePct} />}
            />
            <FactCapsule
              label={t('shared.ncaDetail.details.landMonitoring.ndvi.baselineValuePrevFiveYears', {
                year: ndviBaselineYear?.value,
              })}
              capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
              fact={ndviBaseline}
            />
          </Stack>
          <Stack spacing={2} className='p-2'>
            <div className='h-3 w-full max-w-[222px] rounded-sm bg-[linear-gradient(to_right,#D7191C,_#F15C22,_#F7964A,_#FEE08B,_#FFFFBF,_#D9EF8B,_#B5E384,_#7FCF6F,_#4FB657,_#1A9641)]' />
            <Stack direction='row' className='justify-between'>
              <Stack direction='row' className='justify-between w-full'>
                {[0, 4, 8, 12, 16, 20].map((num) => (
                  <span key={num} className='typography-caption text-text-secondary'>
                    {Intl.NumberFormat(window.navigator.language).format(num)}
                  </span>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      }
    />
  );
};
